import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { openFeedback } from '@/context/FeedbackContext';

interface ErrorBoundaryProps {
 children: React.ReactNode;
 fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

interface ErrorBoundaryState {
 hasError: boolean;
 error: Error | null;
 componentStack?: string;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
 constructor(props: ErrorBoundaryProps) {
 super(props);
 this.state = { hasError: false, error: null, componentStack: undefined };
 }

 static getDerivedStateFromError(error: Error): ErrorBoundaryState {
 return { hasError: true, error };
 }

 componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
 console.error('ErrorBoundary caught an error:', error, errorInfo);
 this.setState({ componentStack: errorInfo?.componentStack });
 }

 resetError = () => {
 this.setState({ hasError: false, error: null, componentStack: undefined });
 };

 reportIssue = () => {
 const err = this.state.error;
 if (!err) return;

 openFeedback({
 source: 'error_boundary',
 autoCaptureScreenshot: true,
 error: {
 message: err.message,
 stack: err.stack,
 componentStack: this.state.componentStack,
 },
 });
 };

 render() {
 if (this.state.hasError) {
 if (this.props.fallback) {
 const FallbackComponent = this.props.fallback;
 return <FallbackComponent error={this.state.error!} resetError={this.resetError} />;
 }

 return (
 <div className="p-4 space-y-4">
 <Alert variant="destructive">
 <AlertTriangle className="h-4 w-4" />
 <AlertDescription>
 {this.state.error?.message || 'Something went wrong. The page encountered an error and couldn\'t be displayed.'}
 </AlertDescription>
 </Alert>
 <div className="flex gap-2 flex-wrap">
 <Button onClick={this.reportIssue} variant="default">
 Report this issue
 </Button>
 <Button onClick={this.resetError} variant="outline">
 Try Again
 </Button>
 <Button onClick={() => window.history.back()} variant="outline">
 Go Back
 </Button>
 <Button onClick={() => window.location.href = '/dashboard'} variant="default">
 Dashboard
 </Button>
 <Button onClick={() => window.location.href = '/dashboard/safe'}>
 Safe Mode
 </Button>
 </div>
 </div>
 );
 }

 return this.props.children;
 }
}

export const RouteErrorFallback: React.FC<{ error: Error; resetError: () => void }> = ({ 
 error, 
 resetError 
}) => (
 <div className="p-4 space-y-4">
 <Alert variant="destructive">
 <AlertTriangle className="h-4 w-4" />
 <AlertDescription>
 {error.message.includes('404') || error.message.includes('not found') 
 ? 'The requested page or resource was not found.'
 : 'An error occurred while loading this page.'
 }
 </AlertDescription>
 </Alert>
 <div className="flex gap-2 flex-wrap">
 <Button onClick={resetError} variant="outline">
 Try Again
 </Button>
 <Button onClick={() => window.history.back()} variant="outline">
 Go Back
 </Button>
 <Button onClick={() => window.location.href = '/dashboard'} variant="default">
 Dashboard
 </Button>
 </div>
 </div>
);